import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LinearRegressionCalculator = () => {
  const [inputData, setInputData] = useState({
    xValues: '',
    yValues: '',
    slope: '',
    intercept: '',
    regressionLine: null
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateRegression = () => {
    const { xValues, yValues } = inputData;

    // Validate input
    if (!xValues || !yValues) {
      setError('Please enter both x and y values');
      return;
    }

    // Convert input to arrays of numbers
    const xArray = xValues.split(',').map(num => num.trim()).filter(num => num !== '');
    const yArray = yValues.split(',').map(num => num.trim()).filter(num => num !== '');
    if (xArray.length === 0 || yArray.length === 0) {
      setError('Please enter valid x and y values');
      return;
    }

    const xNumArray = xArray.map(num => parseFloat(num));
    const yNumArray = yArray.map(num => parseFloat(num));
    if (xNumArray.some(isNaN) || yNumArray.some(isNaN)) {
      setError('Please enter valid x and y values');
      return;
    }

    // Calculate slope (m) and y-intercept (b)
    const n = xNumArray.length;
    const sumX = xNumArray.reduce((acc, val) => acc + val, 0);
    const sumY = yNumArray.reduce((acc, val) => acc + val, 0);
    const sumXY = xNumArray.reduce((acc, val, i) => acc + val * yNumArray[i], 0);
    const sumX2 = xNumArray.reduce((acc, val) => acc + val * val, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Create regression line data
    const regressionLine = {
      labels: xArray,
      datasets: [{
        label: 'Data Points',
        data: yArray,
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        pointRadius: 5,
        pointHoverRadius: 7
      }, {
        label: 'Regression Line',
        data: xNumArray.map(x => slope * x + intercept),
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        pointRadius: 0
      }]
    };

    setResults({
      slope,
      intercept,
      regressionLine
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Linear Regression Calculator | Best-Fit Line Tool</title>
          <meta name="description" content="Calculate the slope and y-intercept of the best-fit line for a set of data points using linear regression." />
          <meta name="keywords" content="linear regression, best-fit line, slope, y-intercept, data analysis" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">📈</div>
            <h1 className="text-3xl font-bold text-gray-800">Linear Regression Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the slope and y-intercept of the best-fit line for a set of data points using linear regression.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">X Values (comma-separated):</label>
              <input
                type="text"
                name="xValues"
                value={inputData.xValues}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter x values (e.g., 1, 2, 3, 4, 5)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Y Values (comma-separated):</label>
              <input
                type="text"
                name="yValues"
                value={inputData.yValues}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter y values (e.g., 2, 4, 6, 8, 10)"
              />
            </div>
          </div>

          <button
            onClick={calculateRegression}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Regression
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Slope (m)</h3>
                  <p className="text-lg">{results.slope}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Y-Intercept (b)</h3>
                  <p className="text-lg">{results.intercept}</p>
                </div>
              </div>

              {results.regressionLine && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Line
                    data={results.regressionLine}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: false,
                          title: {
                            display: true,
                            text: 'Y Values'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'X Values'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Linear Regression'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default LinearRegressionCalculator;